import _, {isArray, isObject, omitBy} from 'lodash';

import {onlyItem} from '../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {AppState} from '../../store/appState';
import {ResourceMap, ResourceMapAffinitiesEnum} from '../../store/appTypes';
import {extractValueAtPath} from './gcHelpers';
import {
  GarbageCollectorCleaningState,
  GarbageCollectorFinishedState,
  GarbageCollectorStagesEnum,
} from './gcTypes';

/*
 * Phase #3: Clean the state by removing unused resources.
 */

export function buildCleanState(
  gcState: GarbageCollectorCleaningState,
  state: AppState,
): GarbageCollectorFinishedState {
  const {mapDefinitions, removalCandidates} = gcState;
  if (mapDefinitions.some(({type, initialAffinity}) => type.getAffinity(state) !== initialAffinity)) {
    throw new Error('ResourceMap affinity changed during runtime.');
  }

  const newState = _(mapDefinitions)
    // Only keep transient maps.
    .filter(({initialAffinity}) => initialAffinity === ResourceMapAffinitiesEnum.TRANSIENT)
    .reduce((currentState, mapDefinition) => {
      const pathsToRemove = removalCandidates.get(mapDefinition.path);
      if (pathsToRemove.length === 0) {
        return currentState;
      }

      const resourceMap = extractValueAtPath(mapDefinition.path, state) as ResourceMap<any>;
      if (!resourceMap) {
        // It's possible that the resource map got removed during runtime.
        return currentState;
      }

      const deletableIds = new Set<string>();
      pathsToRemove.forEach((path) => {
        const resourceId = onlyItem(path);
        if (!resourceId) {
          throw new Error('Unable to get resource id');
        }

        const resource = resourceMap[resourceId];
        if (!resource) {
          // The resource already got removed.
          return;
        }

        const shouldPersist = mapDefinition.getShouldPersist(state, {resource});
        if (shouldPersist) {
          // We should actually persist this resource.
          return;
        }

        deletableIds.add(resourceId);
      });

      const newMap = cleanMap(deletableIds, resourceMap);
      return setValueAtPath(mapDefinition.path, currentState, newMap) as AppState;
    }, state);
  return {
    stage: GarbageCollectorStagesEnum.FINISHED,
    newState,
  };
}

/*
 * Helpers.
 */

function cleanMap(deletableIds: Set<string | number>, resourceMap: ResourceMap<any>) {
  let didOmit = false;

  const cleanResourceMap = omitBy(resourceMap, (_v, key) => {
    const shouldOmit = deletableIds.has(key);
    didOmit = didOmit || shouldOmit;
    return shouldOmit;
  });

  // If nothing was omitted, return the original resource map to minimize object identity changes.
  if (!didOmit) {
    return resourceMap;
  }

  return cleanResourceMap;
}

function setValueAtPath(path: ReadonlyArray<string>, obj: any, value: any): any {
  if (path.length === 0) {
    return value;
  }

  if (!isObject(obj) || isArray(obj)) {
    throw new Error('Cannot set value inside of array.');
  }

  const [propertyName, ...remainingPath] = path;

  const property = obj && (obj as any)[propertyName];
  const newProperty = setValueAtPath(remainingPath, property || {}, value);

  // If the property value is the same, return the original object to minimize identity changes.
  if (property === newProperty) {
    return obj;
  }

  return {
    ...obj,
    [propertyName]: newProperty,
  };
}
